import React from 'react'
import classNames from 'classnames'
import Logo from '#src/components/Logo'
import { Button } from '@deal/components'
import AttributedHtmlText from '#src/components/AttributedHtmlText'
import MetaData, { MetaDataComponentMap } from '#src/components/Path/MetaData'
import InputHeader from '#src/components/Path/components/InputHeader'
import QuestionHeader from '#src/components/Path/components/QuestionHeader'
import ErrorHeader from '#src/components/Path/components/ErrorHeader'
import Interstitial, { InterstitialMap } from '#src/components/Path/Interstitials'
import { extractPageInterstitialData } from '#src/services/path'
import styles from './styles.css'
import { LayoutProps } from '../'
import {
  InterstitialPathFlowPageMetadataType,
  InputTextPathFlowQuestionFragment,
  NumericPathFlowQuestionFragment,
  ListPathFlowQuestionFragment,
  InputTextPathFlowQuestionFieldType
} from '#src/generated/graphql'
import { isValidPhoneNumber } from '#src/services/phone'
import Question, { QuestionComponentMap, PathQuestionProps } from '../../Question'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'

// Questions
import InputTextPathFlowQuestionComponent from '#src/components/Path/components/InputTextPathFlowQuestion'
import IdPathFlowQuestionComponent from '#src/components/Path/components/IdPathFlowQuestion'
import ListPathFlowQuestionComponent from '#src/components/Path/components/ListPathFlowQuestion'
import NumericPathFlowQuestionComponent from '#src/components/Path/components/NumericPathFlowQuestion'
import OAuthEmailPathFlowQuestionComponent from '#src/components/Path/components/OAuthEmailPathFlowQuestion'
import DateRangePathFlowQuestionComponent from '#src/components/Path/components/DateRangePathFlowQuestion'
import DatePathFlowQuestionComponent from '#src/components/Path/components/DatePathFlowQuestion'

// Interstitials
import GenericInterstitial from '#src/components/Path/components/GenericInterstitial'
import GenericWithButtonInterstitial from '#src/components/Path/components/GenericWithButtonInterstitial'
import RequestPushNotificationInterstitial from '#src/components/Path/components/RequestPushNotificationsInterstitial'
import WhatHappensNextInterstitial from '#src/components/Path/components/WhatHappensNextInterstitial'

// Metadata
import TermsPrivacyMedicalMetadata from '#src/components/Path/components/TermsPrivacyMedicalMetadata'
import TermsAndConditionsMetadata from '#src/components/Path/components/TermsAndConditionsMetadata'
import ContactPrivacyMetadata from '#src/components/Path/components/ContactPrivacyMetadata'
import ContactTCPAComplianceMetadata from '#src/components/Path/components/ContactTCPAComplianceMetadata'
import PhoneExplanationModalMetadata from '#src/components/Path/components/PhoneExplanationModalMetadata'
import RatingContactPageMetadataHeader from '#src/components/Path/components/RatingContactPageMetadata/Header'
import RatingContactPageMetadataFooter from '#src/components/Path/components/RatingContactPageMetadata/Footer'

const ListPathFlowQuestionComponentWrapper: React.SFC<
  PathQuestionProps<ListPathFlowQuestionFragment>
> = ({ ...rest }) => {
  return <ListPathFlowQuestionComponent {...rest} display="standard" />
}

const InputTextPathFlowQuestionWrapper: React.SFC<
  PathQuestionProps<InputTextPathFlowQuestionFragment>
> = ({ ...rest }) => {
  return <InputTextPathFlowQuestionComponent {...rest} display="standard" />
}

const NumericPathFlowQuestionWrapper: React.SFC<
  PathQuestionProps<NumericPathFlowQuestionFragment>
> = ({ ...rest }) => {
  return <NumericPathFlowQuestionComponent {...rest} display="standard" />
}

class DefaultLayout extends React.PureComponent<
  LayoutProps & DefaultLayoutProps & FormContextType
> {
  public questionMap: QuestionComponentMap = {
    InputTextPathFlowQuestion: InputTextPathFlowQuestionWrapper,
    IdPathFlowQuestion: IdPathFlowQuestionComponent,
    ListPathFlowQuestion: ListPathFlowQuestionComponentWrapper,
    NumericPathFlowQuestion: NumericPathFlowQuestionWrapper,
    OAuthEmailPathFlowQuestion: OAuthEmailPathFlowQuestionComponent,
    DateRangePathFlowQuestion: DateRangePathFlowQuestionComponent,
    DatePathFlowQuestion: DatePathFlowQuestionComponent
  }

  public interstitialMap: InterstitialMap = {
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_TITLE]: GenericInterstitial,
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_BUTTON]: GenericWithButtonInterstitial,
    [InterstitialPathFlowPageMetadataType.REQUEST_PUSH_NOTIFICATION_PERMISSION]: RequestPushNotificationInterstitial,
    [InterstitialPathFlowPageMetadataType.WHAT_HAPPENS_NEXT]: WhatHappensNextInterstitial
  }

  public metaDataMap: MetaDataComponentMap = {
    TermsPrivacyMedicalPathFlowPageMetadata: TermsPrivacyMedicalMetadata,
    TermsAndConditionsPathFlowPageMetadata: TermsAndConditionsMetadata,
    ContactPrivacyMetadata: ContactPrivacyMetadata,
    ContactTCPAComplianceMetadata: ContactTCPAComplianceMetadata,
    PhoneExplanationModalMetadata: PhoneExplanationModalMetadata
  }

  public componentDidMount() {
    this.props.onUpdateScrollRef(window)
  }

  public render() {
    const {
      areAllQuestionsAnswered,
      areAllQuestionsOptional,
      className,
      isBackButtonHidden,
      isPageLoading,
      isSubmitButtonHidden,
      isFormSubmitting,
      isPageSubmitting,
      isFormValid,
      onPageSubmitted,
      onPageBack,
      pathFlowPage,
      pathFlow,
      questions,
      questionValues,
      hideLogo
    } = this.props
    /**
     * This is a pretty gross implementation of trying to validate a single field out of state on the layout page.
     * The path system just isn't set up to understand questions in this fashion but this experiment warrants something
     * different. The right answer here is to create a new question type on the API side and update everything accordingly.
     * This would involve updating all the path schemas for the new question type that we may not even use. If this
     * experiment proves successful (which it hasn't in the past), then I'll update the API to support a phone question @ktsosno
     */
    const isOptionalPhonePage = questions.every(
      question =>
        question.__typename === 'InputTextPathFlowQuestion' &&
        question.fieldType === InputTextPathFlowQuestionFieldType.TELEPHONE &&
        !question.required
    )

    let isValidPhone = false
    if (isOptionalPhonePage && questions[0]) {
      const questionStateValue = questionValues[questions[0].internalName]

      if (questionStateValue && typeof questionStateValue.value === 'string') {
        isValidPhone = isValidPhoneNumber(questionStateValue.value)
      }
    }

    // Consolidating these but the individual states are available if we want them
    const isLoading = isPageSubmitting || isFormSubmitting || isPageLoading
    const topLevelCategory = pathFlow.category
      ? pathFlow.category.department
        ? pathFlow.category.department.topLevelCategory
        : pathFlow.category
      : undefined
    const interstitialMetadata = extractPageInterstitialData(pathFlowPage.metadata)
    const areActionsHidden = isSubmitButtonHidden && isBackButtonHidden
    const submitButtonText = pathFlowPage.submitButtonText
      ? pathFlowPage.submitButtonText.text
      : 'Submit'
    const skipButtonText = pathFlowPage.skipButtonText ? pathFlowPage.skipButtonText.text : 'Skip'

    // This one has a special rendering treatment
    const ratingWrapperMetaData = pathFlowPage.metadata.find(
      metadata => metadata.__typename === 'RatingContactPageMetadata'
    )

    return (
      <div
        className={classNames('render-path-default', styles.pageContainer, styles.fixedBody)}
        data-testid="path"
      >
        <div className={classNames(styles.pageContainer, className)}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${pathFlowPage.completionPercentage}%` }}
            />
          </div>

          {!hideLogo && (
            <div className={styles.logo}>
              <Logo className={styles.logoMark} color="dark" mark="word" />
            </div>
          )}

          <div className={styles.container}>
            {this.props.errors && (
              <ErrorHeader
                errors={this.props.errors}
                pageSlug={pathFlowPage.slug}
                pathSlug={pathFlow.slug}
                pathFlowId={pathFlow.id}
              />
            )}

            {pathFlowPage.title && (
              <QuestionHeader questionText={pathFlowPage.title} className={styles.title} />
            )}

            {pathFlowPage.notificationTexts.map(notification => (
              <h2 className={styles.subtitle} key={notification.text}>
                <AttributedHtmlText text={notification} />
              </h2>
            ))}

            {interstitialMetadata && (
              <div className={styles.interstitial}>
                <Interstitial
                  key={pathFlowPage.internalName}
                  interstitialMap={this.interstitialMap}
                  pathFlowId={pathFlow.id}
                  pageSlug={pathFlowPage.slug}
                  metadata={interstitialMetadata}
                  submitButtonText={submitButtonText}
                  isBackButtonHidden={isBackButtonHidden}
                  isSubmitting={isLoading}
                  onPageSubmitted={onPageSubmitted}
                  onPageBack={onPageBack}
                />
              </div>
            )}

            {ratingWrapperMetaData && (
              <RatingContactPageMetadataHeader
                categorySlug={topLevelCategory && topLevelCategory.slug}
              />
            )}

            {interstitialMetadata === null && (
              <form
                onSubmit={!isPageSubmitting ? onPageSubmitted : undefined}
                className={styles.form}
              >
                <div className={styles.formContainer}>
                  <div>
                    {questions.map((question, index) => {
                      return (
                        <React.Fragment key={index}>
                          <QuestionHeader
                            questionText={question.questionText}
                            questionSubText={question.questionSubText}
                          />
                          <InputHeader
                            inputLabel={question.inputLabel}
                            inputIconImage={question.inputIconImage}
                          />
                          <Question
                            key={question.internalName}
                            questionIndex={index}
                            question={question}
                            questionComponentMap={this.questionMap}
                          />
                        </React.Fragment>
                      )
                    })}
                  </div>
                  <div>
                    {ratingWrapperMetaData && <RatingContactPageMetadataFooter />}
                    {pathFlowPage.metadata.length > 0 && (
                      <MetaData
                        categorySlug={topLevelCategory && topLevelCategory.slug}
                        metaDataComponents={this.metaDataMap}
                        metaData={pathFlowPage.metadata}
                      />
                    )}
                  </div>
                </div>
                {!areActionsHidden && (
                  <div className={styles.actions}>
                    {isOptionalPhonePage ? (
                      <>
                        <div className={styles.buttons}>
                          <div>
                            <Button
                              className={styles.back}
                              type="button"
                              size="large"
                              variant="secondary"
                              tabIndex={2}
                              onClick={onPageBack}
                              disabled={isLoading}
                            >
                              Back
                            </Button>
                          </div>
                          <div className={styles.submit}>
                            <Button
                              type="button"
                              size="large"
                              tabIndex={1}
                              disabled={!isValidPhone || isLoading}
                              onClick={() => onPageSubmitted()}
                            >
                              {isLoading ? 'Submitting...' : submitButtonText}
                            </Button>
                          </div>
                        </div>
                        <div className={styles.linkCta}>
                          <button
                            className={styles.ratherNotButton}
                            onClick={() => onPageSubmitted()}
                          >
                            Skip for now
                          </button>
                        </div>
                      </>
                    ) : (
                      <div
                        className={classNames(styles.buttons, {
                          [styles.centerContent]: isSubmitButtonHidden
                        })}
                      >
                        {!isBackButtonHidden && (
                          <div>
                            <Button
                              data-testid="back-button"
                              className={styles.back}
                              type="button"
                              size="large"
                              variant="secondary"
                              tabIndex={2}
                              onClick={onPageBack}
                              disabled={isLoading}
                            >
                              {isLoading && isSubmitButtonHidden ? 'Submitting...' : 'Back'}
                            </Button>
                          </div>
                        )}
                        {!isSubmitButtonHidden && (
                          <div className={styles.submit}>
                            <Button
                              data-testid="submit-button"
                              type="button"
                              size="large"
                              tabIndex={1}
                              disabled={!isFormValid || isLoading}
                              onClick={() => onPageSubmitted()}
                            >
                              {isLoading
                                ? 'Submitting...'
                                : areAllQuestionsOptional
                                  ? areAllQuestionsAnswered ? submitButtonText : skipButtonText
                                  : submitButtonText}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    )
  }
}

interface DefaultLayoutProps {
  hideLogo?: boolean
}

const DefaultLayoutWithContext: React.SFC<LayoutProps & DefaultLayoutProps> = ({ ...rest }) => (
  <FormContextConsumer>
    {formData => (formData ? <DefaultLayout {...formData} {...rest} /> : null)}
  </FormContextConsumer>
)

export default DefaultLayoutWithContext
